import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AppContext from "../../../../context/AppContext";

import "./stylesGamePass.css";

import { InfoCircleOutlined } from "@ant-design/icons";
import { message } from "antd";
import dayjs from "dayjs";
import { Calendar, ChevronDown, ChevronUp } from "react-feather";
import KpiCard from "../../../shared/Dashboard/KpiCard/KpiCard";
import Collapse from "../../../shared/Collapse/Collapse";
import RebetLoader from "../../../shared/RebetLoader/RebetLoader";
import { getChallengeRewardLabels } from "../../../../queries/game-pass/challengeRewards";
import { parseSeasonBoundaryDate } from "../../../../queries/game-pass/seasonsMappers";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import MonthlyChallengesView from "./MonthlyChallenges/MonthlyChallengesView";
import { useGetUserProgress } from "../../../../queries/game-pass/useGetUserProgress";
import {
  buildProgressChallengeViewModels,
  buildProgressTaskViewModels,
  normalizeUserProgressResponse,
} from "../../../../queries/game-pass/userProgressMappers";
import { useGetUserEnrollments } from "../../../../queries/game-pass/useGetUserEnrollments";
import { mapUserEnrollmentsToAccordionSeasons } from "../../../../queries/game-pass/userEnrollmentsMappers";
import { useGetUserStats } from "../../../../queries/game-pass/useGetUserStats";
import TaskBetPanel from "./TaskBetPanel/TaskBetPanel";
import { TaskTruncatedCell } from "./TaskTruncatedCell";
import {
  isTaskAlreadyCompletedError,
  usePassUserTask,
} from "../../../../queries/game-pass/usePassUserTask";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import { SEASON_API_STATUS } from "../../../../types/gamePass";

import {
  buildChallengeProgressSummary,
  buildGamePassSummaryKpis,
  buildSeasonProgressSummary,
  buildSeasonProgressSummaryFromProgress,
  buildTaskCompletionStatus,
  formatTaskRewardLabel,
  getChallengePathBadgeLabel,
  getSeasonEnrollmentDateMeta,
  getSeasonPathBadgeLabels,
  getSeasonRecordId,
  getVisibleAccordionSeasons,
  hasHiddenAccordionSeasons,
  isGamePassNotEnrolledQuery,
} from "./gamePassProgressUtils";

const GamePassEmptyState = ({
  title,
  copy,
  testId = "game-pass-empty-state",
  inline = false,
}) => (
  <div
    className={`user-manager-game-pass-empty-state${
      inline ? " user-manager-game-pass-empty-state--inline" : ""
    }`}
    data-testid={testId}
  >
    <h3 className="user-manager-game-pass-empty-state-title">{title}</h3>
    <p className="user-manager-game-pass-empty-state-copy">{copy}</p>
  </div>
);

const GamePassViewToggle = ({
  isMonthlyView,
  onSelectSeasons,
  onSelectMonthly,
}) => {
  const optionClassName = (active) =>
    `user-manager-game-pass-view-toggle-option${
      active ? " user-manager-game-pass-view-toggle-option--active" : ""
    }`;

  return (
    <div
      className="user-manager-game-pass-view-toggle"
      role="tablist"
      aria-label={AppStrings.USER_GAME_PASS_VIEW_TOGGLE_ARIA}
      data-testid="game-pass-view-toggle"
    >
      <button
        type="button"
        role="tab"
        aria-selected={!isMonthlyView}
        className={optionClassName(!isMonthlyView)}
        onClick={onSelectSeasons}
        data-testid="game-pass-view-toggle-seasons"
      >
        {AppStrings.GAME_PASS_SEASONS}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={isMonthlyView}
        className={optionClassName(isMonthlyView)}
        onClick={onSelectMonthly}
        data-testid="game-pass-view-toggle-monthly"
      >
        {AppStrings.MONTHLY_CHALLENGES}
      </button>
    </div>
  );
};

const formatSeasonDateRange = (startDate, endDate) => {
  const start = parseSeasonBoundaryDate(startDate);
  const end = parseSeasonBoundaryDate(endDate);
  if (!start.isValid() || !end.isValid()) {
    return "";
  }
  return `${start.format("M/D")}-${end.format("M/D")}`;
};

const formatEnrollmentDate = (enrolledAt) => {
  if (enrolledAt == null || enrolledAt === "") {
    return "";
  }

  const numericValue = Number(enrolledAt);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    const parsed =
      numericValue > 1e12
        ? dayjs(numericValue)
        : dayjs.unix(numericValue);
    return parsed.isValid() ? parsed.format("MMM D, YYYY") : "";
  }

  const parsed = dayjs(enrolledAt);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "";
};

const getUsernameInitial = (username) => {
  if (typeof username !== "string") return "";
  const trimmed = username.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "";
};

const formatDisplayedUserId = (userId) => {
  if (typeof userId !== "string") return "";
  const trimmed = userId.trim();
  if (!trimmed) return "";
  return trimmed.split("-")[0];
};
const capitalizeLabel = (value) =>
  typeof value === "string" && value.length > 0
    ? `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`
    : "";

const formatSeasonStatus = (status) => {
  if (status === SEASON_API_STATUS.COMPLETED) {
    return "Completed";
  }

  return capitalizeLabel(status);
};

const getSeasonStatusClassName = (status) => {
  if (status === SEASON_API_STATUS.ACTIVE) {
    return "user-manager-game-pass-season-status-active";
  }
  if (status === SEASON_API_STATUS.COMPLETED) {
    return "user-manager-game-pass-season-status-completed";
  }
  return "";
};

const SeasonProgressPanel = ({ label, summary, isLoading, seasonRowKey }) => {
  if (isLoading) {
    return (
      <div
        className="user-manager-game-pass-season-progress-panel"
        data-testid={`game-pass-season-progress-panel-${seasonRowKey}`}
      >
        Loading season progress...
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <div
      className="user-manager-game-pass-season-progress-panel"
      data-testid={`game-pass-season-progress-panel-${seasonRowKey}`}
    >
      <div className="user-manager-game-pass-season-progress-panel-header">
        <span className="user-manager-game-pass-season-progress-panel-label">
          {label}
        </span>
        <div className="user-manager-game-pass-season-progress-panel-stats">
          <span data-testid={`game-pass-season-progress-challenges-${seasonRowKey}`}>
            {`${summary.completedChallenges}/${summary.totalChallenges} challenges`}
          </span>
          <span data-testid={`game-pass-season-progress-tasks-${seasonRowKey}`}>
            {`${summary.completedTasks}/${summary.totalTasks} tasks`}
          </span>
          <span
            className="user-manager-game-pass-season-progress-panel-percent"
            data-testid={`game-pass-season-progress-percent-${seasonRowKey}`}
          >
            {`${summary.percent}%`}
          </span>
        </div>
      </div>
      <div
        className="user-manager-game-pass-season-progress-bar-track"
        data-testid={`game-pass-season-progress-bar-${seasonRowKey}`}
        role="progressbar"
        aria-label={label}
        aria-valuenow={summary.percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="user-manager-game-pass-season-progress-bar-fill"
          style={{ width: `${summary.percent}%` }}
        />
      </div>
    </div>
  );
};

// Matches game-pass-admin pass-task reason validation (backend rejects longer values).
const TASK_PASS_REASON_MAX_LENGTH = 50;

const TaskPassReasonForm = ({
  taskId,
  isLoading,
  onSubmit,
  onCancel,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    setReason("");
  }, [taskId]);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && !isLoading;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    onSubmit(trimmedReason);
  };

  return (
    <div
      className="user-manager-game-pass-task-pass-form"
      data-testid={`game-pass-task-pass-form-${taskId}`}
    >
      <form onSubmit={handleSubmit}>
        <label
          className="user-manager-game-pass-task-pass-form-label"
          htmlFor={`task-pass-reason-${taskId}`}
        >
          {AppStrings.REASON}
        </label>
        <input
          id={`task-pass-reason-${taskId}`}
          className="user-manager-game-pass-task-pass-form-input"
          type="text"
          value={reason}
          maxLength={TASK_PASS_REASON_MAX_LENGTH}
          placeholder={AppStrings.LEAVE_A_COMMENT}
          onChange={(event) => setReason(event.target.value)}
          autoComplete="off"
          autoFocus
          data-testid={`game-pass-task-pass-reason-${taskId}`}
        />
        <div className="user-manager-game-pass-task-pass-form-actions">
          <button
            type="button"
            className="user-manager-game-pass-task-pass-form-cancel"
            onClick={onCancel}
            data-testid={`game-pass-task-pass-cancel-${taskId}`}
          >
            {AppStrings.CANCEL}
          </button>
          <button
            type="submit"
            className="user-manager-game-pass-task-pass-form-submit"
            disabled={!canSubmit}
            data-testid={`game-pass-task-pass-submit-${taskId}`}
          >
            {isLoading
              ? AppStrings.ADDING_MANUAL_PASS
              : AppStrings.USER_GAME_PASS_TASK_PASS}
          </button>
        </div>
      </form>
    </div>
  );
};

const TaskRow = ({
  task,
  taskId,
  progressTask,
  completionStatus,
  open,
  onToggle,
  userId,
  canPassTasks = true,
  isPassFormOpen = false,
  onPassClick,
  onPassSubmit,
  onPassCancel,
  isPassLoading = false,
}) => {
  const actionRef = useRef(null);
  const taskName = task.name?.trim() || AppStrings.NOT_AVAILABLE;
  const hasTaskName = Boolean(task.name?.trim());
  const taskDescription = task.description?.trim() || AppStrings.NOT_AVAILABLE;
  const hasTaskDescription = Boolean(task.description?.trim());
  const manualPassReason = completionStatus.reason?.trim();
  const passReasonLabel =
    completionStatus.variant === "manual-pass" && manualPassReason
      ? manualPassReason
      : AppStrings.EMPTY_REASON;
  const hasPassReason =
    completionStatus.variant === "manual-pass" && Boolean(manualPassReason);
  const rewardLabel = formatTaskRewardLabel(task);
  const isPassDisabled =
    !canPassTasks ||
    completionStatus.variant !== "incomplete" ||
    !task?.id ||
    isPassLoading;
  const handlePassClick = () => {
    if (isPassDisabled) {
      return;
    }
    onPassClick?.();
  };

  useEffect(() => {
    if (!isPassFormOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (actionRef.current && !actionRef.current.contains(event.target)) {
        onPassCancel?.();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isPassFormOpen, onPassCancel]);

  return (
    <div
      className={`user-manager-game-pass-task-row${
        isPassFormOpen ? " user-manager-game-pass-task-row--pass-form-open" : ""
      }`.trim()}
      data-testid={`game-pass-task-${taskId}`}
    >
      <div className="user-manager-game-pass-task-trigger">
        <button
          type="button"
          className="user-manager-game-pass-task-expand-trigger"
          onClick={() => onToggle(taskId)}
          data-testid={`game-pass-task-trigger-${taskId}`}
        >
          <span className="user-manager-game-pass-task-chevron" aria-hidden="true">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
          <TaskTruncatedCell
            text={taskName}
            className="user-manager-game-pass-task-cell user-manager-game-pass-task-name"
            testId={`game-pass-task-name-${taskId}`}
            showTooltip={hasTaskName}
          />
          <TaskTruncatedCell
            text={taskDescription}
            className="user-manager-game-pass-task-cell user-manager-game-pass-task-description"
            testId={`game-pass-task-description-${taskId}`}
            showTooltip={hasTaskDescription}
          />
          <TaskTruncatedCell
            text={passReasonLabel}
            className={`user-manager-game-pass-task-cell user-manager-game-pass-task-reason${
              passReasonLabel === AppStrings.EMPTY_REASON
                ? " user-manager-game-pass-task-reason--empty"
                : ""
            }`.trim()}
            testId={`game-pass-task-reason-${taskId}`}
            showTooltip={hasPassReason}
          />
          <span className="user-manager-game-pass-task-cell user-manager-game-pass-task-completed">
            <span
              className={`user-manager-game-pass-task-status-pill user-manager-game-pass-task-status-pill-${completionStatus.variant}`}
              data-testid={`game-pass-task-status-${taskId}`}
            >
              {completionStatus.label}
            </span>
          </span>
          <span
            className="user-manager-game-pass-task-cell user-manager-game-pass-task-reward"
            data-testid={`game-pass-task-reward-${taskId}`}
          >
            {rewardLabel}
          </span>
        </button>
        <div
          ref={actionRef}
          className="user-manager-game-pass-task-action"
        >
          {isPassFormOpen && (
            <TaskPassReasonForm
              taskId={taskId}
              isLoading={isPassLoading}
              onSubmit={onPassSubmit}
              onCancel={onPassCancel}
            />
          )}
          <button
            type="button"
            className="user-manager-game-pass-task-pass-button"
            disabled={isPassDisabled || isPassFormOpen}
            onClick={handlePassClick}
            data-testid={`game-pass-task-pass-${taskId}`}
          >
            {AppStrings.USER_GAME_PASS_TASK_PASS}
          </button>
        </div>
      </div>
      <Collapse
        open={open}
        className="user-manager-game-pass-task-collapse"
        data-testid={`game-pass-task-collapse-${taskId}`}
      >
        <TaskBetPanel
          taskId={taskId}
          userId={userId}
          task={task}
          progressTask={progressTask}
          open={open}
        />
      </Collapse>
    </div>
  );
};

const ChallengeTasksList = ({
  challenge,
  challengeId,
  challengeProgressEntry,
  userId,
  seasonId,
  canPassTasks = true,
  expandAll = false,
  expandNonce = 0,
}) => {
  const [openTaskIds, setOpenTaskIds] = useState({});
  const [passFormTaskId, setPassFormTaskId] = useState(null);
  const [passingTaskId, setPassingTaskId] = useState(null);
  const passUserTaskMutation = usePassUserTask();
  const challengeTasks = useMemo(
    () => buildProgressTaskViewModels(challengeProgressEntry),
    [challengeProgressEntry],
  );
  // Drive the task rows from the season-level "Expand all" control. When it is
  // active we open every task (including ones that arrive after the challenge
  // loads); an explicit collapse command (detected via the nonce) closes them.
  const lastTaskExpandNonceRef = useRef(expandNonce);
  useEffect(() => {
    const isExpandCommand = lastTaskExpandNonceRef.current !== expandNonce;
    lastTaskExpandNonceRef.current = expandNonce;
    if (expandAll) {
      setOpenTaskIds((current) => {
        const next = { ...current };
        challengeTasks.forEach((task) => {
          next[task.id] = true;
        });
        return next;
      });
    } else if (isExpandCommand) {
      setOpenTaskIds({});
    }
  }, [expandAll, expandNonce, challengeTasks]);
  const toggleTask = useCallback((taskId) => {
    setOpenTaskIds((current) => ({
      ...current,
      [taskId]: !current[taskId],
    }));
  }, []);
  const handleOpenPassForm = useCallback((task) => {
    if (!canPassTasks) {
      return;
    }
    const configuredTaskId = task?.id;
    if (!configuredTaskId) {
      return;
    }
    setPassFormTaskId(configuredTaskId);
  }, [canPassTasks]);

  const handleCancelPassForm = useCallback(() => {
    setPassFormTaskId(null);
  }, []);

  const handlePassTask = useCallback(
    (task, reason) => {
      const configuredTaskId = task?.id;
      const trimmedReason = reason?.trim();
      if (!canPassTasks) {
        message.error(AppStrings.USER_GAME_PASS_TASK_PASS_INACTIVE_SEASON);
        return;
      }
      if (!userId || !seasonId || !configuredTaskId || !trimmedReason) {
        return;
      }

      setPassingTaskId(configuredTaskId);
      passUserTaskMutation.mutate(
        {
          userId,
          taskId: configuredTaskId,
          seasonId,
          reason: trimmedReason,
          challengeId,
          challenge,
        },
        {
          onSuccess: () => {
            message.success(AppStrings.USER_GAME_PASS_TASK_PASS_SUCCESS);
            setPassFormTaskId(null);
          },
          onError: (error) => {
            if (isTaskAlreadyCompletedError(error)) {
              message.info(
                AppStrings.USER_GAME_PASS_TASK_PASS_ALREADY_COMPLETED,
              );
              setPassFormTaskId(null);
              return;
            }

            message.error(
              getApiErrorMessage(
                error,
                AppStrings.USER_GAME_PASS_TASK_PASS_ERROR,
              ),
            );
          },
          onSettled: () => {
            setPassingTaskId(null);
          },
        },
      );
    },
    [canPassTasks, passUserTaskMutation, seasonId, userId, challengeId, challenge],
  );

  return (
    <div
      className="user-manager-game-pass-challenge-tasks-section"
      data-testid={`game-pass-challenge-tasks-${challengeId}`}
    >
      <span className="user-manager-game-pass-challenge-tasks-label">
        {AppStrings.TASKS}
      </span>
      {challengeTasks.length === 0 ? (
        <div
          className="user-manager-game-pass-challenge-tasks-empty"
          data-testid={`game-pass-challenge-tasks-empty-${challengeId}`}
        >
          {AppStrings.USER_GAME_PASS_CHALLENGE_NO_TASKS}
        </div>
      ) : (
        <div
          className={`user-manager-game-pass-challenge-tasks-table${
            passFormTaskId
              ? " user-manager-game-pass-challenge-tasks-table--pass-form-open"
              : ""
          }`.trim()}
        >
          <div
            className="user-manager-game-pass-challenge-tasks-header"
            data-testid={`game-pass-challenge-tasks-header-${challengeId}`}
          >
            <span aria-hidden="true" />
            <span>{AppStrings.USER_GAME_PASS_TASK_NAME}</span>
            <span>{AppStrings.DESCRIPTION}</span>
            <span>{AppStrings.REASON}</span>
            <span>{AppStrings.SEASON_STATUS_COMPLETED}</span>
            <span>{AppStrings.REWARD}</span>
            <span>{AppStrings.ACTION}</span>
          </div>
          <div className="user-manager-game-pass-challenge-tasks-body">
            {challengeTasks.map((task) => {
              const taskId = task.id;
              const progressTask = task.progressTask;
              const completionStatus = buildTaskCompletionStatus(progressTask);

              return (
                <TaskRow
                  key={taskId}
                  task={task}
                  taskId={taskId}
                  progressTask={progressTask}
                  completionStatus={completionStatus}
                  open={Boolean(openTaskIds[taskId])}
                  onToggle={toggleTask}
                  userId={userId}
                  canPassTasks={canPassTasks}
                  isPassFormOpen={passFormTaskId === task.id}
                  onPassClick={() => handleOpenPassForm(task)}
                  onPassSubmit={(reason) => handlePassTask(task, reason)}
                  onPassCancel={handleCancelPassForm}
                  isPassLoading={
                    passingTaskId === task.id && passUserTaskMutation.isPending
                  }
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ChallengePanel = ({
  challenge,
  challengeId,
  challengeProgressEntry,
  userId,
  seasonId,
  canPassTasks = true,
  expandAll = false,
  expandNonce = 0,
}) => {
  const rewardLabels = getChallengeRewardLabels(challenge);
  const description = challenge.description?.trim() ?? "";
  const hasDescription = description.length > 0;
  const hasRewards = rewardLabels.length > 0;

  return (
    <div
      className="user-manager-game-pass-challenge-panel"
      data-testid={`game-pass-challenge-panel-${challengeId}`}
    >
      {hasDescription && (
        <p
          className="user-manager-game-pass-challenge-description"
          data-testid={`game-pass-challenge-description-${challengeId}`}
        >
          {description}
        </p>
      )}
      {hasRewards && (
        <div
          className="user-manager-game-pass-challenge-rewards-section"
          data-testid={`game-pass-challenge-rewards-${challengeId}`}
        >
          <span className="user-manager-game-pass-challenge-rewards-label">
            {AppStrings.CHALLENGE_REWARDS_LABEL}
          </span>
          <div className="user-manager-game-pass-challenge-reward-badges">
            {rewardLabels.map((rewardLabel, index) => (
              <span
                key={`${challengeId}-reward-${index}`}
                className="user-manager-game-pass-challenge-reward-badge"
                data-testid={`game-pass-challenge-reward-badge-${challengeId}-${index}`}
              >
                {rewardLabel}
              </span>
            ))}
          </div>
        </div>
      )}
      <ChallengeTasksList
        challenge={challenge}
        challengeId={challengeId}
        challengeProgressEntry={challengeProgressEntry}
        userId={userId}
        seasonId={seasonId}
        canPassTasks={canPassTasks}
        expandAll={expandAll}
        expandNonce={expandNonce}
      />
    </div>
  );
};

const ChallengeRow = ({
  challenge,
  challengeId,
  challengeProgressEntry,
  challengeProgressSummary,
  open,
  onToggle,
  userId,
  seasonId,
  canPassTasks = true,
  expandAll = false,
  expandNonce = 0,
}) => {
  const challengeBadgeLabel = getChallengePathBadgeLabel(challenge);
  const challengeStatusClassName =
    challengeProgressSummary.status === "completed"
      ? "user-manager-game-pass-challenge-status-pill-completed"
      : "user-manager-game-pass-challenge-status-pill-progress";

  return (
    <div
      className="user-manager-game-pass-challenge-row"
      data-testid={`game-pass-challenge-${challengeId}`}
    >
      <button
        type="button"
        className="user-manager-game-pass-challenge-trigger"
        onClick={() => onToggle(challengeId)}
        data-testid={`game-pass-challenge-trigger-${challengeId}`}
      >
        <span className="user-manager-game-pass-challenge-trigger-left">
          <span className="user-manager-game-pass-challenge-name-group">
            <span className="user-manager-game-pass-challenge-name">
              {challenge.name}
            </span>
            {challengeBadgeLabel && (
              <span
                className={`user-manager-game-pass-challenge-path-badge${
                  challengeBadgeLabel === "Premium"
                    ? " user-manager-game-pass-season-path-badge--premium"
                    : ""
                }`.trim()}
                data-testid={`game-pass-challenge-badge-${challengeId}-${challengeBadgeLabel.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {challengeBadgeLabel}
              </span>
            )}
          </span>
        </span>
        <span className="user-manager-game-pass-challenge-trigger-right">
          {challengeProgressSummary.progressLabel && (
            <span
              className="user-manager-game-pass-challenge-progress"
              data-testid={`game-pass-challenge-progress-${challengeId}`}
            >
              {challengeProgressSummary.progressLabel}
            </span>
          )}
          <span
            className={`user-manager-game-pass-challenge-status-pill ${challengeStatusClassName}`.trim()}
            data-testid={`game-pass-challenge-status-${challengeId}`}
          >
            {challengeProgressSummary.statusLabel}
          </span>
          <span className="user-manager-game-pass-challenge-chevron" aria-hidden="true">
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </span>
      </button>
      <Collapse
        open={open}
        className="user-manager-game-pass-challenge-collapse"
        data-testid={`game-pass-challenge-collapse-${challengeId}`}
      >
        <ChallengePanel
          challenge={challenge}
          challengeId={challengeId}
          challengeProgressEntry={challengeProgressEntry}
          userId={userId}
          seasonId={seasonId}
          canPassTasks={canPassTasks}
          expandAll={expandAll}
          expandNonce={expandNonce}
        />
      </Collapse>
    </div>
  );
};

const SeasonChallengesList = ({
  progressChallenges = [],
  enrollment,
  isLoading,
  userId,
  seasonId,
  canPassTasks = true,
  expandAll = false,
  expandNonce = 0,
}) => {
  const [openChallengeIds, setOpenChallengeIds] = useState({});
  const toggleChallenge = useCallback((challengeId) => {
    setOpenChallengeIds((current) => ({
      ...current,
      [challengeId]: !current[challengeId],
    }));
  }, []);
  // Season challenges load lazily once the season expands, so "Expand all" has
  // to open them both on the command and as the data arrives; the nonce lets a
  // collapse command reset them without wiping manual toggles on a refetch.
  const lastChallengeExpandNonceRef = useRef(expandNonce);
  useEffect(() => {
    const isExpandCommand = lastChallengeExpandNonceRef.current !== expandNonce;
    lastChallengeExpandNonceRef.current = expandNonce;
    if (expandAll) {
      setOpenChallengeIds((current) => {
        const next = { ...current };
        progressChallenges.forEach((challenge) => {
          next[challenge.id] = true;
        });
        return next;
      });
    } else if (isExpandCommand) {
      setOpenChallengeIds({});
    }
  }, [expandAll, expandNonce, progressChallenges]);

  if (isLoading) {
    return (
      <div className="user-manager-game-pass-season-panel">
        Loading challenges...
      </div>
    );
  }

  if (progressChallenges.length === 0) {
    return (
      <div className="user-manager-game-pass-season-panel">
        No challenges for this season
      </div>
    );
  }

  return (
    <div className="user-manager-game-pass-challenges">
      <div className="user-manager-game-pass-challenges-body">
        {progressChallenges.map((challenge, index) => {
          const challengeId = challenge.id;
          const challengeProgressEntry = challenge.progressEntry;
          const challengeProgressSummary = buildChallengeProgressSummary(
            challengeProgressEntry,
            {
              challengeIndex: index,
              enrollment,
              challenge,
            },
          );

          return (
            <ChallengeRow
              key={challengeId}
              challenge={challenge}
              challengeId={challengeId}
              challengeProgressEntry={challengeProgressEntry}
              challengeProgressSummary={challengeProgressSummary}
              open={Boolean(openChallengeIds[challengeId])}
              onToggle={toggleChallenge}
              userId={userId}
              seasonId={seasonId}
              canPassTasks={canPassTasks}
              expandAll={expandAll}
              expandNonce={expandNonce}
            />
          );
        })}
      </div>
    </div>
  );
};

const SeasonRow = ({
  season,
  open,
  onToggle,
  userId,
  expandAll = false,
  expandNonce = 0,
}) => {
  const seasonId = getSeasonRecordId(season);
  const seasonRowKey = seasonId || season.display_name || season.name || "season";
  const isActiveSeason = season.status === SEASON_API_STATUS.ACTIVE;
  const isCompletedSeason = season.status === SEASON_API_STATUS.COMPLETED;
  // UX half of the read-only gamepass guard in PrivateAdminPanelInstance,
  // riding the existing canPassTasks chain.
  const { adminReadOnly } = useContext(AppContext) ?? {};
  const [hasOpenedSeason, setHasOpenedSeason] = useState(open);
  useEffect(() => {
    if (open) {
      setHasOpenedSeason(true);
    }
  }, [open]);
  // Completed seasons load progress eagerly (even while collapsed) so the
  // heading status badge can resolve completed (green) vs incomplete (red)
  // without the admin having to expand every past season — including rows
  // revealed by "See more". Active seasons stay lazy since their badge label
  // never depends on progress.
  const shouldLoadSeasonData = hasOpenedSeason || isCompletedSeason;
  const seasonProgressQuery = useGetUserProgress(userId, seasonId, {
    enabled: shouldLoadSeasonData && Boolean(seasonId),
  });
  const normalizedSeasonProgress = useMemo(
    () =>
      seasonProgressQuery.data
        ? normalizeUserProgressResponse(seasonProgressQuery.data)
        : undefined,
    [seasonProgressQuery.data],
  );
  const progressEntries = useMemo(
    () => normalizedSeasonProgress?.data?.progress ?? [],
    [normalizedSeasonProgress?.data?.progress],
  );
  const progressChallenges = useMemo(
    () => buildProgressChallengeViewModels(progressEntries),
    [progressEntries],
  );
  const seasonEnrollment =
    normalizedSeasonProgress?.data?.enrollment ?? season.userEnrollment;
  const seasonEnrollmentDateMeta = getSeasonEnrollmentDateMeta(
    season,
    seasonEnrollment,
  );
  const seasonEnrollmentDateLabel = seasonEnrollmentDateMeta
    ? formatEnrollmentDate(seasonEnrollmentDateMeta.timestamp)
    : "";
  const isSeasonProgressLoading =
    shouldLoadSeasonData &&
    Boolean(seasonId) &&
    (seasonProgressQuery.isLoading || seasonProgressQuery.isFetching) &&
    !normalizedSeasonProgress;
  const seasonProgressSummary = useMemo(() => {
    const progressPayload = normalizedSeasonProgress?.data;
    if (!progressPayload) {
      return null;
    }

    const isEnrolledInSeason = Boolean(progressPayload.enrollment);
    if (!isEnrolledInSeason && progressEntries.length === 0) {
      return null;
    }

    if (isSeasonProgressLoading) {
      return null;
    }

    const totalChallenges =
      season.challenge_count ?? progressChallenges.length;
    const totalTasks = season.task_count;

    if (shouldLoadSeasonData && progressEntries.length > 0) {
      return buildSeasonProgressSummaryFromProgress({
        progressEntries,
        enrollment: seasonEnrollment,
        totalChallenges,
        totalTasks,
      });
    }

    return buildSeasonProgressSummary({
      progressEntries,
      totalChallenges,
      totalTasks,
    });
  }, [
    isSeasonProgressLoading,
    progressChallenges.length,
    progressEntries,
    season.challenge_count,
    season.task_count,
    seasonEnrollment,
    normalizedSeasonProgress,
    shouldLoadSeasonData,
  ]);
  const seasonBadgeLabels = getSeasonPathBadgeLabels(season);
  const seasonProgressLabel = isActiveSeason
    ? AppStrings.USER_GAME_PASS_CURRENT_SEASON_PROGRESS
    : AppStrings.USER_GAME_PASS_SEASON_PROGRESS;
  // "Resolved" once the season's progress query has settled — whether it came
  // back with data, empty, or errored (e.g. a 404 for a season with no
  // challenges). We only need to know the season isn't a confirmed 100% to mark
  // it incomplete, so anything short of a loaded full-completion counts.
  const hasResolvedSeasonProgress =
    isCompletedSeason &&
    !isSeasonProgressLoading &&
    (Boolean(normalizedSeasonProgress) || seasonProgressQuery.isError);
  const isSeasonFullyCompleted =
    hasResolvedSeasonProgress &&
    seasonProgressSummary != null &&
    seasonProgressSummary.totalChallenges > 0 &&
    seasonProgressSummary.percent === 100;
  // A past season with no challenges (or that never resolved any progress) can
  // never be completed, so it reads as incomplete alongside partially-finished
  // seasons.
  const isSeasonIncomplete =
    hasResolvedSeasonProgress && !isSeasonFullyCompleted;
  const seasonStatusClassName = isSeasonIncomplete
    ? "user-manager-game-pass-season-status-incomplete"
    : `${getSeasonStatusClassName(season.status)}${
        isSeasonFullyCompleted
          ? " user-manager-game-pass-season-status-completed-full"
          : ""
      }`.trim();
  const seasonStatusLabel = isSeasonIncomplete
    ? AppStrings.SEASON_STATUS_INCOMPLETE
    : formatSeasonStatus(season.status);

  return (
    <div
      className="user-manager-game-pass-season-row"
      data-testid={`game-pass-season-${seasonRowKey}`}
    >
      <button
        type="button"
        className="user-manager-game-pass-season-trigger"
        onClick={() => onToggle(seasonRowKey)}
        data-testid={`game-pass-season-trigger-${seasonRowKey}`}
      >
        <span className="user-manager-game-pass-season-trigger-left">
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          <span className="user-manager-game-pass-season-name-group">
            <span className="user-manager-game-pass-season-name">
              {season.display_name || season.name}
            </span>
            {seasonBadgeLabels.map((badgeLabel) =>
              badgeLabel === "Premium" ? (
                <span
                  key={badgeLabel}
                  className="user-manager-game-pass-season-premium-meta"
                >
                  <span
                    className="user-manager-game-pass-season-path-badge user-manager-game-pass-season-path-badge--premium"
                    data-testid={`game-pass-season-badge-${seasonRowKey}-premium`}
                  >
                    {badgeLabel}
                  </span>
                  {seasonEnrollmentDateLabel && (
                    <span
                      className="user-manager-game-pass-season-premium-purchased"
                      data-testid={`game-pass-season-premium-purchased-${seasonRowKey}`}
                    >
                      {`${seasonEnrollmentDateMeta.label}: ${seasonEnrollmentDateLabel}`}
                    </span>
                  )}
                </span>
              ) : (
                <span
                  key={badgeLabel}
                  className="user-manager-game-pass-season-path-badge"
                  data-testid={`game-pass-season-badge-${seasonRowKey}-${badgeLabel.toLowerCase()}`}
                >
                  {badgeLabel}
                </span>
              ),
            )}
          </span>
        </span>
        <span className="user-manager-game-pass-season-meta">
          <span
            className={`user-manager-game-pass-season-status ${seasonStatusClassName}`.trim()}
            data-testid={`game-pass-season-status-${seasonRowKey}`}
          >
            {season.status === SEASON_API_STATUS.ACTIVE && (
              <span
                className="user-manager-game-pass-season-status-dot"
                aria-hidden="true"
              />
            )}
            {seasonStatusLabel}
          </span>
          <span className="user-manager-game-pass-season-dates">
            <Calendar size={14} />
            <span>{formatSeasonDateRange(season.start_date, season.end_date)}</span>
          </span>
        </span>
      </button>
      <Collapse
        open={open}
        className="user-manager-game-pass-season-collapse"
        data-testid={`game-pass-season-collapse-${seasonRowKey}`}
      >
        {(seasonProgressSummary || isSeasonProgressLoading) && (
          <SeasonProgressPanel
            label={seasonProgressLabel}
            summary={seasonProgressSummary}
            isLoading={isSeasonProgressLoading}
            seasonRowKey={seasonRowKey}
          />
        )}
        <SeasonChallengesList
          progressChallenges={progressChallenges}
          enrollment={seasonEnrollment}
          isLoading={isSeasonProgressLoading}
          userId={userId}
          seasonId={seasonId}
          canPassTasks={isActiveSeason && adminReadOnly !== true}
          expandAll={expandAll}
          expandNonce={expandNonce}
        />
      </Collapse>
    </div>
  );
};

const getSeasonAccordionKey = (season) =>
  season.id ??
  season.season_id ??
  season.display_name ??
  season.name ??
  "season";

const GamePass = ({ userData }) => {
  const enrollmentsQuery = useGetUserEnrollments(userData?.user_id);
  const enrolledAccordionSeasons = useMemo(
    () => mapUserEnrollmentsToAccordionSeasons(enrollmentsQuery.data ?? []),
    [enrollmentsQuery.data],
  );
  const activeSeasonId = useMemo(() => {
    const enrolledActiveSeason = enrolledAccordionSeasons.find(
      (season) => season.status === SEASON_API_STATUS.ACTIVE,
    );
    return enrolledActiveSeason
      ? getSeasonRecordId(enrolledActiveSeason)
      : undefined;
  }, [enrolledAccordionSeasons]);
  const progressQuery = useGetUserProgress(userData?.user_id, activeSeasonId);
  const shouldSkipActiveSeasonStats = isGamePassNotEnrolledQuery(progressQuery);
  const statsQuery = useGetUserStats(userData?.user_id, activeSeasonId, {
    enabled: !shouldSkipActiveSeasonStats,
  });
  const [openSeasonIds, setOpenSeasonIds] = useState({});
  const [showAllKpis, setShowAllKpis] = useState(false);
  const [showAllSeasons, setShowAllSeasons] = useState(false);
  // "Expand all" opens every season, challenge and task at once. The nonce is a
  // command counter the nested lists watch to distinguish a deliberate expand /
  // collapse from an incidental data refetch (see SeasonChallengesList).
  const [expandAll, setExpandAll] = useState(false);
  const [expandNonce, setExpandNonce] = useState(0);
  const [activeGamePassView, setActiveGamePassView] = useState("seasons");

  const kpis = useMemo(
    () => buildGamePassSummaryKpis(statsQuery.data),
    [statsQuery.data],
  );
  const headerProgressData = useMemo(() => {
    if (progressQuery.data?.data?.enrollment) {
      return progressQuery.data;
    }

    const firstEnrolledSeason = enrolledAccordionSeasons.find(
      (season) => season.userEnrollment?.username || season.userEnrollment?.user_id,
    );

    if (firstEnrolledSeason?.userEnrollment) {
      return {
        data: {
          enrollment: firstEnrolledSeason.userEnrollment,
          progress: [],
        },
      };
    }

    return progressQuery.data;
  }, [enrolledAccordionSeasons, progressQuery.data]);
  const viewedUsername = headerProgressData?.data?.enrollment?.username;
  const viewedUserId =
    headerProgressData?.data?.enrollment?.user_id || userData?.user_id;
  const displayedUserId = formatDisplayedUserId(viewedUserId);
  const enrolledOn = formatEnrollmentDate(
    headerProgressData?.data?.enrollment?.enrolled_at,
  );
  const viewedUserInitial = getUsernameInitial(viewedUsername);
  const tierLabel = headerProgressData?.data?.enrollment?.is_premium
    ? "Premium"
    : "Free";
  const visibleKpis = showAllKpis ? kpis : kpis.slice(0, 4);
  const shouldShowMoreKpis = kpis.length > 4;
  const visibleAccordionSeasons = useMemo(
    () => getVisibleAccordionSeasons(enrolledAccordionSeasons, showAllSeasons),
    [enrolledAccordionSeasons, showAllSeasons],
  );
  const shouldShowMoreSeasons = useMemo(
    () => hasHiddenAccordionSeasons(enrolledAccordionSeasons),
    [enrolledAccordionSeasons],
  );
  const hasResolvedSeasonEnrollment =
    !enrollmentsQuery.isLoading && !enrollmentsQuery.isFetching;

  const isLoading =
    enrollmentsQuery.isLoading ||
    enrollmentsQuery.isFetching ||
    progressQuery.isLoading ||
    progressQuery.isFetching ||
    statsQuery.isLoading ||
    statsQuery.isFetching;
  const isSeasonsLoading =
    enrollmentsQuery.isLoading || enrollmentsQuery.isFetching;
  const isNotEnrolledInAnySeason =
    hasResolvedSeasonEnrollment && enrolledAccordionSeasons.length === 0;
  const isNotEnrolledInActiveSeason =
    Boolean(activeSeasonId) &&
    enrolledAccordionSeasons.length > 0 &&
    isGamePassNotEnrolledQuery(progressQuery);
  const errorMessage = getApiErrorMessage(
    enrollmentsQuery.error ?? progressQuery.error,
    enrollmentsQuery.error?.message ??
      progressQuery.error?.message ??
      "",
  );
  const shouldShowProgressError =
    progressQuery.isError && progressQuery.error?.response?.status !== 404;
  const shouldShowError =
    (enrollmentsQuery.isError || shouldShowProgressError) && errorMessage;
  // Stats are season-scoped to the active season; without one there is nothing
  // to summarise, but past enrolled seasons should still render below.
  const shouldShowActiveSeasonStats =
    Boolean(activeSeasonId) && !isNotEnrolledInActiveSeason;
  const toggleSeason = useCallback((seasonId) => {
    setOpenSeasonIds((current) => ({
      ...current,
      [seasonId]: !current[seasonId],
    }));
  }, []);

  const handleToggleExpandAll = useCallback(() => {
    setExpandAll((current) => !current);
    setExpandNonce((nonce) => nonce + 1);
  }, []);

  // Keep every visible season open while "Expand all" is active. Driving this
  // from an effect (rather than the click handler) means seasons revealed later
  // via "See more" auto-expand too; the nonce lets a collapse command reset
  // without wiping manual toggles on an incidental enrollment refetch.
  const lastSeasonExpandNonceRef = useRef(expandNonce);
  useEffect(() => {
    const isExpandCommand = lastSeasonExpandNonceRef.current !== expandNonce;
    lastSeasonExpandNonceRef.current = expandNonce;
    if (expandAll) {
      setOpenSeasonIds((current) => {
        const next = { ...current };
        visibleAccordionSeasons.forEach((season) => {
          next[getSeasonAccordionKey(season)] = true;
        });
        return next;
      });
    } else if (isExpandCommand) {
      setOpenSeasonIds({});
    }
  }, [expandAll, expandNonce, visibleAccordionSeasons]);

  const isMonthlyView = activeGamePassView === "monthly";
  const viewToggle = (
    <GamePassViewToggle
      isMonthlyView={isMonthlyView}
      onSelectSeasons={() => setActiveGamePassView("seasons")}
      onSelectMonthly={() => setActiveGamePassView("monthly")}
    />
  );
  const canExpandAll =
    !isMonthlyView && !isSeasonsLoading && visibleAccordionSeasons.length > 0;
  const seasonsHeader = (
    <div
      className="user-manager-game-pass-seasons-header"
      data-testid="game-pass-seasons-header"
    >
      {viewToggle}
      {canExpandAll && (
        <button
          type="button"
          className="user-manager-game-pass-expand-all"
          onClick={handleToggleExpandAll}
          aria-pressed={expandAll}
          data-testid="game-pass-expand-all-toggle"
        >
          <span
            className="user-manager-game-pass-expand-all-icon"
            aria-hidden="true"
          >
            {expandAll ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
          {expandAll ? AppStrings.COLLAPSE_ALL : AppStrings.EXPAND_ALL}
        </button>
      )}
    </div>
  );

  if (isMonthlyView) {
    return (
      <div className="user-manager-game-pass-container">
        {viewToggle}
        <MonthlyChallengesView userId={userData?.user_id} />
      </div>
    );
  }

  if (isNotEnrolledInAnySeason) {
    return (
      <div className="user-manager-game-pass-container">
        {viewToggle}
        <GamePassEmptyState
          title={AppStrings.USER_GAME_PASS_NOT_ENROLLED_ANY_SEASON_TITLE}
          copy={AppStrings.USER_GAME_PASS_NOT_ENROLLED_ANY_SEASON_COPY}
        />
      </div>
    );
  }

  return (
    <div className="user-manager-game-pass-container">
      {shouldShowError && (
        <div
          className="user-manager-game-pass-error"
          data-testid="game-pass-error"
        >
          {errorMessage}
        </div>
      )}
      <section
        className="user-manager-game-pass-overview"
        data-testid="game-pass-overview"
      >
        {viewedUsername && (
          <div
            className="user-manager-game-pass-viewed-user"
            data-testid="game-pass-viewed-user"
          >
            <div className="user-manager-game-pass-viewed-user-content">
              <div className="user-manager-game-pass-viewed-user-identity">
                <div
                  className="user-manager-game-pass-viewed-user-chip"
                  data-testid="game-pass-viewed-user-chip"
                >
                  <div
                    className="user-manager-game-pass-viewed-user-avatar"
                    data-testid="game-pass-viewed-user-avatar"
                    aria-hidden="true"
                  >
                    {viewedUserInitial}
                  </div>
                  <span className="user-manager-game-pass-viewed-user-name">
                    {`@${viewedUsername}`}
                  </span>
                </div>
                {displayedUserId && (
                  <span
                    className="user-manager-game-pass-viewed-user-id"
                    data-testid="game-pass-viewed-user-id"
                  >
                    {`#${displayedUserId}`}
                  </span>
                )}
                <span
                  className="user-manager-game-pass-viewed-user-tier"
                  data-testid="game-pass-viewed-user-tier"
                >
                  {tierLabel}
                </span>
              </div>
              {enrolledOn && (
                <div
                  className="user-manager-game-pass-viewed-user-meta"
                  data-testid="game-pass-viewed-user-meta"
                >
                  <span className="user-manager-game-pass-viewed-user-meta-label">
                    {AppStrings.USER_GAME_PASS_ENROLLED_ON}
                  </span>
                  <span>{enrolledOn}</span>
                </div>
              )}
            </div>
          </div>
        )}
        {!shouldShowActiveSeasonStats ? (
          <GamePassEmptyState
            title={
              activeSeasonId
                ? AppStrings.USER_GAME_PASS_NOT_ENROLLED_ACTIVE_SEASON_TITLE
                : AppStrings.USER_GAME_PASS_NO_ACTIVE_SEASON_SUMMARY_TITLE
            }
            copy={AppStrings.USER_GAME_PASS_NOT_ENROLLED_ACTIVE_SEASON_COPY}
            testId="game-pass-summary-empty-state"
            inline
          />
        ) : (
          <>
            <div
              className="user-manager-game-pass-summary"
              data-testid="game-pass-summary"
            >
              {visibleKpis.map((kpi) => (
                <div
                  key={kpi.id}
                  className="user-manager-game-pass-summary-card"
                  data-testid={`game-pass-summary-card-${kpi.id}`}
                >
                  <KpiCard
                    title={kpi.title}
                    value={kpi.value}
                    backgroundColor="#1A1A22"
                    maximumFractionDigits={kpi.maximumFractionDigits ?? 0}
                    displayAsRawText={Boolean(kpi.displayAsRawText)}
                    hidePercentage
                    isApiRunning={isLoading}
                  />
                </div>
              ))}
            </div>
            {shouldShowMoreKpis && (
              <button
                type="button"
                className="user-manager-game-pass-summary-toggle"
                onClick={() => setShowAllKpis((current) => !current)}
                data-testid="game-pass-summary-toggle"
              >
                {showAllKpis ? "See less" : "See more"}
              </button>
            )}
          </>
        )}
      </section>
      {seasonsHeader}
      {isSeasonsLoading ? (
        <div
          className="user-manager-game-pass-seasons-loader"
          data-testid="game-pass-seasons-loader"
        >
          <RebetLoader height="100px" width="100px" />
        </div>
      ) : (
        <>
          <div
            className="user-manager-game-pass-seasons-enrollment-note"
            data-testid="game-pass-enrolled-seasons-note"
          >
            <InfoCircleOutlined
              className="user-manager-game-pass-seasons-enrollment-note-icon"
              aria-hidden="true"
            />
            <p className="user-manager-game-pass-seasons-enrollment-note-text">
              {AppStrings.USER_GAME_PASS_ENROLLED_SEASONS_NOTE}
            </p>
          </div>
          <div
            className="user-manager-game-pass-seasons"
            data-testid="game-pass-seasons"
          >
            {visibleAccordionSeasons.map((season) => {
              const seasonRowKey = getSeasonAccordionKey(season);
              const isOpen = Boolean(openSeasonIds[seasonRowKey]);
              return (
                <SeasonRow
                  key={seasonRowKey}
                  season={season}
                  open={isOpen}
                  onToggle={toggleSeason}
                  userId={userData?.user_id}
                  expandAll={expandAll}
                  expandNonce={expandNonce}
                />
              );
            })}
            {shouldShowMoreSeasons && (
              <button
                type="button"
                className="user-manager-game-pass-seasons-toggle"
                onClick={() => setShowAllSeasons((current) => !current)}
                data-testid="game-pass-seasons-toggle"
              >
                {showAllSeasons ? "See less" : "See more"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GamePass;
